import { Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { SessionLockService } from '../services/session-lock.service';

/**
 * Přidává Bearer token ke každému požadavku a při 401 se pokusí access token
 * tiše obnovit přes refresh token (viz AuthService.refreshToken), request pak
 * zopakuje s novým tokenem. Když obnovení definitivně selže, relaci ZAMKNE
 * (SessionLockService) — nad stránkou se objeví lock screen pro rychlé odemčení
 * heslem místo tvrdého odhlášení a ztráty kontextu.
 * Souběžné 401 (víc požadavků naráz) spustí obnovení jen jednou — ostatní počkají
 * na jeho výsledek přes refreshSubject.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  constructor(private injector: Injector) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authService = this.injector.get(AuthService);
    const sessionLock = this.injector.get(SessionLockService);
    const token = localStorage.getItem('token');

    // Relace je zamčená (čeká na odemčení heslem) — další požadavky nemá smysl
    // pouštět ani obnovovat, jen je necháme selhat, dokud uživatel neodemkne.
    if (sessionLock.isLocked && !request.url.includes('/connect/token')) {
      return throwError(() => new Error('Relace je zamčená'));
    }

    return next.handle(this.addToken(request, token)).pipe(
      catchError((error: HttpErrorResponse) => {
        // 401 na samotném /connect/token (login i refresh) neřešíme obnovením —
        // to je selhání přihlášení/obnovení, jen ho propaganeme (login) / odhlásíme.
        if (error.status === 401 && !request.url.includes('/connect/token')) {
          return this.handle401(request, next, authService, sessionLock);
        }
        return throwError(() => error);
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string | null): HttpRequest<any> {
    return token
      ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : request;
  }

  private handle401(request: HttpRequest<any>, next: HttpHandler, authService: AuthService, sessionLock: SessionLockService): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      // Obnovení už běží (jiný souběžný požadavek ho spustil) — počkej na nový token.
      return this.refreshSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.addToken(request, token)))
      );
    }

    this.isRefreshing = true;
    this.refreshSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap(success => {
        this.isRefreshing = false;
        if (success) {
          const newToken = localStorage.getItem('token');
          this.refreshSubject.next(newToken);
          return next.handle(this.addToken(request, newToken));
        }
        // Obnovení selhalo → zamkni relaci (lock screen), neodhlašuj natvrdo.
        sessionLock.lock();
        return throwError(() => new Error('Relace vypršela'));
      }),
      catchError(err => {
        this.isRefreshing = false;
        sessionLock.lock();
        return throwError(() => err);
      })
    );
  }
}
